/**
 * useBulkEditDeals - Hook para edição em massa de deals
 * Chama RPC bulk_edit_deals com validação cross-workspace server-side
 * 
 * Versão: 1.0.0
 * RELEASE: 2026-04-09
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('BulkEditDeals');

/**
 * Dados para edição em massa
 */
export interface BulkEditDealsData {
  /** IDs dos deals a editar */
  dealIds: string[];
  /** Pipeline destino (mesmo workspace obrigatório) */
  pipelineId?: string;
  /** Fase destino (deve pertencer ao pipeline) */
  phaseId?: string;
  /** Nova origem do lead */
  source?: string;
  /** IDs de tags a adicionar */
  tagsToAdd?: string[];
  /** IDs de tags a remover */
  tagsToRemove?: string[];
}

/**
 * Hook para edição em massa de deals via RPC
 * Invalida queries de deals após sucesso
 */
export function useBulkEditDeals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkEditDealsData) => {
      logger.info('Executando edição em massa', {
        dealCount: data.dealIds.length,
        hasFields: !!(data.pipelineId || data.phaseId || data.source),
        tagsToAdd: data.tagsToAdd?.length || 0,
        tagsToRemove: data.tagsToRemove?.length || 0,
      });

      // Monta JSONB de atualizações (apenas campos preenchidos)
      const updates: Record<string, unknown> = {};
      if (data.pipelineId) updates.pipeline_id = data.pipelineId;
      if (data.phaseId) updates.phase_id = data.phaseId;
      if (data.source) updates.source = data.source;
      if (data.tagsToAdd && data.tagsToAdd.length > 0) updates.tags_to_add = data.tagsToAdd;
      if (data.tagsToRemove && data.tagsToRemove.length > 0) updates.tags_to_remove = data.tagsToRemove;

      // Verifica se há algo para atualizar
      if (Object.keys(updates).length === 0) {
        logger.warn('Nenhum campo para atualizar');
        return 0;
      }

      const { data: result, error } = await supabase.rpc('bulk_edit_deals', {
        p_deal_ids: data.dealIds,
        p_updates: updates as unknown as Record<string, never>,
      });

      if (error) {
        logger.error('Erro na edição em massa', error);
        throw error;
      }

      return result as number;
    },
    onSuccess: (count, variables) => {
      // Invalida queries globais de deals para atualizar UI
      queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['deal-tag-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['deal-list-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });

      // Invalida cache individual de cada deal editado (modal, sidebar, etc.)
      for (const dealId of variables.dealIds) {
        queryClient.invalidateQueries({ queryKey: ['crm-deal', dealId] });
        queryClient.invalidateQueries({ queryKey: ['deal-modal-data', dealId] });
      }

      toast.success(`${count} negócio(s) atualizado(s) com sucesso`);
    },
    onError: (error: Error) => {
      logger.error('Falha na edição em massa', error);
      toast.error(error.message || 'Erro ao editar negócios em massa');
    },
  });
}
